import { FirebaseError } from 'firebase/app';
import { reportClientError } from '../services/clientErrorReporting';

/**
 * Error types that can occur in the application
 */
export type AppErrorType =
  | 'auth'
  | 'firestore'
  | 'storage'
  | 'network'
  | 'validation'
  | 'permission'
  | 'not_found'
  | 'unknown';

/**
 * Structured error object
 */
export interface AppError {
  type: AppErrorType;
  message: string;
  userMessage: string;
  code?: string;
  originalError?: unknown;
  retryable?: boolean;
}

/**
 * Firebase Auth Error Messages
 */
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/operation-not-allowed': 'This operation is not allowed. Please contact support.',
  'auth/weak-password': 'Please use a stronger password (at least 6 characters).',
  'auth/user-disabled': 'This account has been disabled. Please contact support.',
  'auth/user-not-found': 'No account found with this email. Please sign up first.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/invalid-credential': 'Invalid credentials. Please check your email and password.',
  'auth/account-exists-with-different-credential': 'An account already exists with this email but different sign-in method.',
  'auth/popup-closed-by-user': 'Sign-in popup was closed. Please try again.',
  'auth/popup-blocked': 'Sign-in popup was blocked. Please allow popups and try again.',
  'auth/requires-recent-login': 'For security, please sign in again to complete this action.',
};

/**
 * Firebase Firestore Error Messages
 */
const FIRESTORE_ERROR_MESSAGES: Record<string, string> = {
  'permission-denied': 'You don\'t have permission to access this resource.',
  'not-found': 'The requested resource was not found.',
  'already-exists': 'This resource already exists.',
  'resource-exhausted': 'Too many requests. Please try again later.',
  'failed-precondition': 'Operation cannot be performed. Please check your data.',
  'aborted': 'Operation was cancelled. Please try again.',
  'out-of-range': 'Operation is out of valid range.',
  'unimplemented': 'This feature is not yet available.',
  'internal': 'Internal error occurred. Please try again.',
  'unavailable': 'Service is temporarily unavailable. Please try again later.',
  'data-loss': 'Data loss detected. Please contact support immediately.',
  'unauthenticated': 'Please sign in to continue.',
  'invalid-argument': 'Invalid data provided. Please check your input.',
  'deadline-exceeded': 'Operation timed out. Please try again.',
  'cancelled': 'Operation was cancelled.',
};

/**
 * Parse Firebase errors and return user-friendly messages
 */
function parseFirebaseError(error: FirebaseError): AppError {
  // Check if it's an Auth error
  if (error.code.startsWith('auth/')) {
    const userMessage = AUTH_ERROR_MESSAGES[error.code] || 'Authentication error occurred. Please try again.';
    return {
      type: 'auth',
      message: error.message,
      userMessage,
      code: error.code,
      originalError: error,
      retryable: !error.code.includes('invalid') && !error.code.includes('not-found'),
    };
  }

  // Check if it's a Firestore error
  const firestoreMessage = FIRESTORE_ERROR_MESSAGES[error.code];
  if (firestoreMessage) {
    return {
      type: 'firestore',
      message: error.message,
      userMessage: firestoreMessage,
      code: error.code,
      originalError: error,
      retryable: ['unavailable', 'deadline-exceeded', 'aborted', 'resource-exhausted'].includes(error.code),
    };
  }

  // Unknown Firebase error
  return {
    type: 'unknown',
    message: error.message,
    userMessage: 'Something went wrong. Please try again.',
    code: error.code,
    originalError: error,
    retryable: true,
  };
}

/**
 * Parse network errors
 */
function parseNetworkError(error: Error): AppError {
  const isNetworkError =
    error.message.includes('network') ||
    error.message.includes('fetch') ||
    error.message.includes('CORS') ||
    error.message.includes('Failed to fetch');

  if (isNetworkError) {
    return {
      type: 'network',
      message: error.message,
      userMessage: 'Network error. Please check your internet connection.',
      originalError: error,
      retryable: true,
    };
  }

  return {
    type: 'unknown',
    message: error.message,
    userMessage: 'Something went wrong. Please try again.',
    originalError: error,
    retryable: true,
  };
}

/**
 * Main error handler function
 * Converts any error into a structured AppError
 */
export function handleError(error: unknown): AppError {
  // Handle null/undefined
  if (!error) {
    return {
      type: 'unknown',
      message: 'Unknown error occurred',
      userMessage: 'Something went wrong. Please try again.',
      retryable: true,
    };
  }

  // Handle Firebase errors
  if (error instanceof FirebaseError) {
    return parseFirebaseError(error);
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return parseNetworkError(error);
  }

  // Handle string errors
  if (typeof error === 'string') {
    return {
      type: 'unknown',
      message: error,
      userMessage: error,
      originalError: error,
      retryable: true,
    };
  }

  // Handle object errors
  if (typeof error === 'object' && error !== null) {
    const err = error as any;
    return {
      type: 'unknown',
      message: err.message || JSON.stringify(error),
      userMessage: err.userMessage || 'Something went wrong. Please try again.',
      code: err.code,
      originalError: error,
      retryable: err.retryable ?? true,
    };
  }

  // Fallback for unknown error types
  return {
    type: 'unknown',
    message: String(error),
    userMessage: 'Something went wrong. Please try again.',
    originalError: error,
    retryable: true,
  };
}

/**
 * Log error to console and report it to the backend log (POST /client-errors).
 *
 * Only the classified error is reported — never `context`, which callers may
 * fill with form values. Validation errors are the user's input, not a fault,
 * so they stay local.
 */
export function logError(error: AppError, context?: Record<string, any>) {
  console.error('[Error Handler]', {
    type: error.type,
    message: error.message,
    userMessage: error.userMessage,
    code: error.code,
    context,
    timestamp: new Date().toISOString(),
  });

  if (error.type !== 'validation') {
    const original = error.originalError instanceof Error ? error.originalError : undefined;
    reportClientError(
      {
        message: `[${error.type}${error.code ? `/${error.code}` : ''}] ${error.message}`,
        stack: original?.stack,
      },
      { level: 'warning', source: 'handler' }
    );
  }
}

/**
 * Retry helper for retryable operations
 */
export async function retryOperation<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delayMs: number = 1000
): Promise<T> {
  let lastError: unknown;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      const appError = handleError(error);

      // Don't retry if error is not retryable
      if (!appError.retryable) {
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxRetries) {
        throw error;
      }

      // Wait before retrying (exponential backoff)
      const delay = delayMs * Math.pow(2, attempt - 1);
      console.log(`Retry attempt ${attempt}/${maxRetries} after ${delay}ms...`);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}

/**
 * Create validation error
 */
export function createValidationError(message: string, field?: string): AppError {
  return {
    type: 'validation',
    message: `Validation error: ${message}`,
    userMessage: message,
    code: 'validation-error',
    originalError: { field },
    retryable: false,
  };
}

/**
 * Create permission error
 */
export function createPermissionError(action: string): AppError {
  return {
    type: 'permission',
    message: `Permission denied: ${action}`,
    userMessage: `You don\'t have permission to ${action}.`,
    code: 'permission-denied',
    retryable: false,
  };
}

/**
 * Create not found error
 */
export function createNotFoundError(resource: string): AppError {
  return {
    type: 'not_found',
    message: `${resource} not found`,
    userMessage: `The requested ${resource} was not found.`,
    code: 'not-found',
    retryable: false,
  };
}

